import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getVideoById, getRelatedVideos, formatPlayCount } from "@/utils/video";
import VideoCard from "@/components/base/VideoCard";
import { useFavorites } from "@/hooks/useFavorites";

function setMeta(title: string, description: string) {
  document.title = title;
  const tag = document.querySelector('meta[name="description"]');
  if (tag) tag.setAttribute("content", description);
}

export default function DetailPage() {
  const { videoId } = useParams();
  const video = getVideoById(videoId);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [playModalOpen, setPlayModalOpen] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (video) {
      setMeta(`${video.title} - 色色视频`, video.description.slice(0, 120));
    }
  }, [video]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!video) {
    return (
      <div className="mx-auto flex w-full max-w-[1400px] flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
          <i className="ri-emotion-sad-line text-3xl" aria-hidden="true"></i>
        </span>
        <h1 className="mt-5 font-heading text-3xl text-foreground-950">视频不存在</h1>
        <p className="mt-3 text-sm text-foreground-500">你访问的视频可能已下线，返回首页继续探索吧。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-home-4-line text-lg" aria-hidden="true"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelatedVideos(video.id, 4);
  const favorited = isFavorite(video.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("视频链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  const handleFavorite = () => {
    const next = toggleFavorite(video.id);
    setToast(next ? "已加入我的收藏" : "已取消收藏");
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6">
      <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line text-base" aria-hidden="true"></i>
        <Link to={`/category/${video.category}`} className="transition-colors hover:text-primary-600">
          {video.categoryName}
        </Link>
        <i className="ri-arrow-right-s-line text-base" aria-hidden="true"></i>
        <span className="line-clamp-1 text-foreground-900">{video.title}</span>
      </nav>

      <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-foreground-950">
            <img
              src={video.cover}
              alt={`${video.title} 视频封面大图`}
              title={`${video.title} - 色色视频`}
              className="h-full w-full object-top"
            />
            <div className="absolute inset-0 bg-foreground-950/40"></div>
            <button
              type="button"
              onClick={() => setPlayModalOpen(true)}
              aria-label="播放视频（演示）"
              className="absolute inset-0 flex flex-col items-center justify-center gap-3"
            >
              <span className="flex h-20 w-20 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform hover:scale-110">
                <i className="ri-play-fill text-4xl" aria-hidden="true"></i>
              </span>
              <span className="rounded-full bg-foreground-950/70 px-4 py-1.5 text-xs text-background-100">
                演示播放 · 点击查看说明
              </span>
            </button>
          </div>

          <h1 className="mt-6 font-heading text-3xl leading-tight text-foreground-950 md:text-4xl">
            {video.title}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
            <span className="flex items-center gap-1.5">
              <i className="ri-play-circle-line text-base text-primary-500" aria-hidden="true"></i>
              {formatPlayCount(video.playCount)} 次播放
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-base" aria-hidden="true"></i>
              时长 {video.duration}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-base" aria-hidden="true"></i>
              {video.uploadTime}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-price-tag-3-line text-base" aria-hidden="true"></i>
              {video.categoryName}
            </span>
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleFavorite}
              className={`flex items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-medium transition-colors ${
                favorited
                  ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                  : "border border-background-300 bg-background-50 text-foreground-700 hover:border-accent-300 hover:text-accent-600"
              }`}
            >
              <i className={favorited ? "ri-heart-fill text-lg" : "ri-heart-line text-lg"} aria-hidden="true"></i>
              {favorited ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-share-forward-line text-lg" aria-hidden="true"></i>
              分享
            </button>
            <Link
              to={`/category/${video.category}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-secondary-100 px-5 py-2.5 text-sm font-medium text-secondary-700 transition-colors hover:bg-secondary-200"
            >
              <i className="ri-apps-2-line text-lg" aria-hidden="true"></i>
              同类视频
            </Link>
          </div>

          <div className="mt-8 rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="font-heading text-xl text-foreground-950">视频简介</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">{video.description}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              <span className="rounded-full bg-primary-50 px-3 py-1 text-xs text-primary-700">{video.tags.genre}</span>
              <span className="rounded-full bg-accent-50 px-3 py-1 text-xs text-accent-700">{video.tags.region}</span>
              <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-700">{video.tags.year}</span>
              <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-700">{video.tags.language}</span>
            </div>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <h2 className="flex items-center gap-2 font-heading text-xl text-foreground-950">
            <i className="ri-movie-2-line text-primary-500" aria-hidden="true"></i>
            相关推荐
          </h2>
          <div className="mt-4 flex flex-col gap-4">
            {related.map((item) => (
              <Link
                key={item.id}
                to={`/detail/${item.id}`}
                className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-2 transition-colors hover:border-primary-300 hover:bg-primary-50/40"
              >
                <div className="relative h-[68px] w-[120px] shrink-0 overflow-hidden rounded-md bg-secondary-100">
                  <img
                    src={item.cover}
                    alt={`${item.title} 封面`}
                    title={`${item.title} - 色色视频`}
                    className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute bottom-1 right-1 rounded bg-foreground-950/75 px-1.5 py-0.5 text-[10px] text-background-50">
                    {item.duration}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                    {item.title}
                  </h3>
                  <p className="mt-1.5 text-xs text-foreground-500">{item.categoryName} · {item.tags.genre}</p>
                  <p className="mt-1 text-xs text-foreground-400">{formatPlayCount(item.playCount)} 次播放</p>
                </div>
              </Link>
            ))}
          </div>
        </aside>
      </div>

      <section className="mt-12">
        <h2 className="mb-5 flex items-center gap-2 font-heading text-2xl text-foreground-950">
          <span className="h-6 w-1.5 rounded-full bg-primary-500" aria-hidden="true"></span>
          更多精彩视频
        </h2>
        <div className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 lg:grid-cols-4">
          {getRelatedVideos(video.id, 8)
            .slice(0, 4)
            .map((item, index) => (
              <VideoCard key={item.id} video={item} index={index} />
            ))}
        </div>
      </section>

      {playModalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/70 px-4"
          onClick={() => setPlayModalOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-xl bg-background-50 p-6 text-center"
            onClick={(event) => event.stopPropagation()}
          >
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-50 text-primary-600">
              <i className="ri-film-line text-3xl" aria-hidden="true"></i>
            </span>
            <h3 className="mt-4 font-heading text-2xl text-foreground-950">演示状态</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              当前为前端演示状态，真实播放功能待开发。本站不提供真实的视频播放服务，敬请期待后续上线。
            </p>
            <button
              type="button"
              onClick={() => setPlayModalOpen(false)}
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              我知道了
            </button>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[70] -translate-x-1/2 rounded-full bg-foreground-950 px-5 py-2.5 text-sm text-background-50 shadow-none">
          {toast}
        </div>
      )}
    </div>
  );
}